"use strict";
if (window.lastViewedProductId) {
  let recentlyViewedProducts = getRecentlyViewedProducts();

  // Remove any entries of the same product. We only care about the latest (current) view.
  recentlyViewedProducts = recentlyViewedProducts.filter(
    (viewedProduct) => viewedProduct.id !== lastViewedProductId,
  );

  recentlyViewedProducts.push({
    timestamp: Date.now(),
    id: window.lastViewedProductId,
  });

  recentlyViewedProducts = saveRecentlyViewedProducts(recentlyViewedProducts);
}

function getRecentlyViewedProducts() {
  let recentlyViewedProducts = localStorage.getItem("recentlyViewedProducts");
  try {
    recentlyViewedProducts = JSON.parse(recentlyViewedProducts);
  } catch (error) {
    recentlyViewedProducts = [];
  }

  if (!recentlyViewedProducts) {
    recentlyViewedProducts = [];
  }

  return recentlyViewedProducts;
}

function saveRecentlyViewedProducts(recentlyViewedProducts) {
  // Keep twice the amount of the max number (12) of recently viewed products that can be displayed, in case some were removed.
  recentlyViewedProducts = recentlyViewedProducts.slice(-24);

  localStorage.setItem("recentlyViewedProducts", JSON.stringify(recentlyViewedProducts));

  return recentlyViewedProducts;
}

if (!customElements.get("recently-viewed-products")) {
  class RecentlyViewedProducts extends HTMLElement {
    constructor() {
      super();

      this.recentlyViewedProducts = getRecentlyViewedProducts();
      this.columns = parseInt(this.getAttribute("data-columns"), 10);
      this.fetched = false;
    }

    connectedCallback() {
      if (this.recentlyViewedProducts && !this.fetched) {
        this.fetch();
      }
    }

    fetch() {
      if (this.recentlyViewedProducts) {
        // Remove currently viewed product from array.
        const foundIndex = this.recentlyViewedProducts.findIndex(
          (product) => product.id === window.lastViewedProductId,
        );
        if (foundIndex > -1) {
          this.recentlyViewedProducts.splice(foundIndex, 1);
        }
      }

      // recentlyViewedProducts is a stack, so newer items are at the bottom. Reverse.
      const searchString = this.recentlyViewedProducts
        .toReversed()
        .map((product) => `id:${product.id}`)
        .join(" OR ");

      const url = `${window.routes.search_url}?section_id=${
        this.dataset.sectionId
      }&type=product&q=${encodeURIComponent(searchString)}`;

      fetch(url)
        .then((response) => response.text())
        .then((content) => {
          const html = document.createElement("div");

          html.innerHTML = content;

          this.innerHTML = html.querySelector("recently-viewed-products").innerHTML;
          this.fetched = true;

          this.querySelectorAll("product-card").forEach((product) => {
            const slider = product.closest("carousel-slider .grid-carousel");
            const productId = product.dataset.productId;
            const viewEntry = this.recentlyViewedProducts.find(
              (productEntry) => productEntry.id === productId,
            );
            if (!viewEntry) {
              return;
            }

            const rtf = new Intl.RelativeTimeFormat(window.recentlyViewedStrings.currentLanguage, {
              numeric: "auto",
            });
            const now = Date.now();

            const diffSec = (now - viewEntry.timestamp) / 1000;
            const diffMin = Math.trunc(diffSec / 60);
            const diffHours = Math.trunc(diffMin / 60);
            const diffDays = Math.trunc(diffHours / 24);
            const diffMonths = Math.trunc(diffDays / 30);
            const diffYears = Math.trunc(diffMonths / 12);

            let relativeTime = window.recentlyViewedStrings.justNow;
            if (diffYears >= 1) {
              relativeTime = rtf.format(-diffYears, "years");
            } else if (diffMonths >= 1) {
              relativeTime = rtf.format(-diffMonths, "months");
            } else if (diffDays >= 1) {
              relativeTime = rtf.format(-diffDays, "days");
            } else if (diffHours >= 1) {
              relativeTime = rtf.format(-diffHours, "hours");
            } else if (diffMin >= 1) {
              relativeTime = rtf.format(-diffMin, "minutes");
            } else {
              relativeTime = window.recentlyViewedStrings.lessThanMinute;
            }

            const textElement = document.createElement("div");
            textElement.classList.add("card-product-recently-viewed-time");
            textElement.innerHTML = `<span>${relativeTime}</span> <button type="button" class="button-text-link card-product-recently-viewed-dismiss">${window.recentlyViewedStrings.remove}</button>`;

            textElement.querySelector("button").addEventListener("click", (e) => {
              let items = getRecentlyViewedProducts();
              items = items.filter((item) => item.id.toString() !== productId.toString());
              saveRecentlyViewedProducts(items);

              const visibleItems = product.closest(".flickity-slider")?.children;
              product.parentElement.remove();

              if (items.length < 2) {
                this.style.display = "none";
              }

              if (slider) {
                const flickity = Flickity.data(slider);
                flickity.resize();

                this.sliderNav = this.querySelector("carousel-navigation");
                if (visibleItems?.length <= this.columns) {
                  this.sliderNav ? (this.sliderNav.style.display = "none") : null;
                }
              }
            });

            product.querySelector(".card-footer")?.append?.(textElement);
            if (slider) {
              const flickity = Flickity.data(slider);
              flickity?.resize();
              this.sliderNav = this.querySelector("carousel-navigation");

              if (this.recentlyViewedProducts.length <= this.columns) {
                this.sliderNav ? (this.sliderNav.style.display = "none") : null;
              }
            }
          });
        });
    }
  }

  customElements.define("recently-viewed-products", RecentlyViewedProducts);
}

// Novoom: show Kanlux article number + EAN exactly once on product pages.
(() => {
  if (window.__novoomKanluxIdentifiersLoaded) return;
  window.__novoomKanluxIdentifiersLoaded = true;

  const initKanluxIdentifiers = async () => {
    if (!window.location.pathname.includes('/products/')) return;

    let product;
    try {
      const response = await fetch(`${window.location.pathname}.js`, {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return;
      product = await response.json();
    } catch (error) {
      return;
    }

    if ((product.vendor || '').trim().toLowerCase() !== 'kanlux') return;

    const getCurrentVariant = () => {
      const variantInput = document.querySelector('form[action*="/cart/add"] [name="id"]');
      const variantId = variantInput?.value;
      return product.variants.find((variant) => String(variant.id) === String(variantId)) || product.variants[0];
    };

    const ensureIdentifiers = () => {
      if (document.getElementById('NovoomKanluxIdentifiers')) return true;

      const productArea = document.querySelector('[id^="ProductPage-"]') || document;
      const title = productArea.querySelector('h1');
      if (!title) return false;

      const titleBlock = title.closest('[class*="product-block"], [class*="block"]') || title.parentElement;
      if (!titleBlock || !titleBlock.parentElement) return false;

      const root = document.createElement('div');
      root.id = 'NovoomKanluxIdentifiers';
      root.className = 'novoom-product-identifiers';
      root.style.cssText = 'display:flex;flex-wrap:wrap;gap:6px;align-items:center;margin:4px 0 8px;color:#5f6368;font-size:12.5px;line-height:1.4;';
      root.innerHTML = '<span style="display:inline-flex;gap:4px"><strong>Artikelnummer:</strong><span data-kanlux-sku></span></span><span data-kanlux-separator aria-hidden="true" style="color:#c7cdd3">|</span><span data-kanlux-ean-item style="display:inline-flex;gap:4px"><strong>EAN:</strong><span data-kanlux-ean></span></span>';

      titleBlock.insertAdjacentElement('afterend', root);
      return true;
    };

    const updateIdentifiers = () => {
      if (!ensureIdentifiers()) return;
      const root = document.getElementById('NovoomKanluxIdentifiers');
      const variant = getCurrentVariant();
      if (!root || !variant) return;

      const sku = variant.sku || '';
      const barcode = variant.barcode || '';
      root.querySelector('[data-kanlux-sku]').textContent = sku;
      root.querySelector('[data-kanlux-ean]').textContent = barcode;
      root.querySelector('[data-kanlux-ean-item]').style.display = barcode ? 'inline-flex' : 'none';
      root.querySelector('[data-kanlux-separator]').style.display = barcode ? '' : 'none';
    };

    if (!ensureIdentifiers()) {
      let attempts = 0;
      const timer = window.setInterval(() => {
        attempts += 1;
        if (ensureIdentifiers() || attempts >= 20) {
          window.clearInterval(timer);
          updateIdentifiers();
        }
      }, 150);
    } else {
      updateIdentifiers();
    }

    document.addEventListener('change', (event) => {
      if (event.target.closest('[id^="ProductPage-"]')) {
        window.setTimeout(updateIdentifiers, 0);
      }
    });
    document.addEventListener('variant:change', updateIdentifiers);
    document.addEventListener('variantChange', updateIdentifiers);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initKanluxIdentifiers, { once: true });
  } else {
    initKanluxIdentifiers();
  }
})();
